import { NetworkStatus, useQuery } from "@apollo/client";
import {
  Button,
  Copyable,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  PrimaryFullScreenSpinner,
  TransferCard,
} from "@repo/ui";
import { prettyPrintEnum } from "@repo/utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Suspense } from "react";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { OrganisationCard } from "~/components/Organisation/OrganisationCard";
import { graphql } from "~/gql/client";
import type { CalastoneOrderRowFragment } from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/calastone-orders")({
  head: () => opsHead("Calastone Orders"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const PAGE_SIZE = 20;

const columns: ColumnDef<CalastoneOrderRowFragment>[] = [
  {
    id: "shortRef",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Ref" />
    ),
    cell: ({ row }) => (
      <span className="font-mono text-tertiary">
        <Copyable copyText={row.original.id}>#{row.original.shortRef}</Copyable>
      </span>
    ),
  },
  {
    id: "transfer",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Transfer" />
    ),
    cell: ({ row }) => (
      <TransferCard
        transfer={row.original.transfer}
        variant="TRANSFER_OUT"
        subText={["DATE", "PAYMENT_REFERENCE"]}
      />
    ),
  },
  {
    id: "organisation",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Organisation" />
    ),
    cell: ({ row }) => {
      const org = row.original.transfer.organisation;
      if (!org) {
        return null;
      }
      return (
        <OrganisationCard
          organisation={org}
          linkTo={`/organisations/${org.id}`}
        />
      );
    },
  },
  {
    id: "status",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => (
      <span className="text-secondary text-sm">
        {prettyPrintEnum(row.original.status)}
      </span>
    ),
  },
  {
    id: "createdAt",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created" />
    ),
    cell: ({ row }) => (
      <DateUI date={row.original.createdAt} variant="DATETIME_TZ" />
    ),
  },
  {
    id: "updatedAt",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Updated" />
    ),
    cell: ({ row }) => (
      <DateUI date={row.original.updatedAt} variant="DATETIME_TZ" />
    ),
  },
];

function View() {
  const navigate = useNavigate();

  const { data, loading, networkStatus, fetchMore } = useQuery(
    graphql(`
      query AdminCalastoneOrders($after: String, $first: Int!) {
        calastoneOrders(after: $after, first: $first) {
          pageInfo {
            endCursor
            hasNextPage
          }
          calastoneOrders {
            ...CalastoneOrderRow
          }
        }
      }

      fragment CalastoneOrderRow on CalastoneOrder {
        id
        shortRef
        status
        createdAt
        updatedAt
        transfer {
          id
          ...TransferCardFields
          organisation {
            id
            ...OrganisationCardFields
          }
        }
      }
    `),
    {
      variables: { after: null, first: PAGE_SIZE },
      notifyOnNetworkStatusChange: true,
    },
  );

  const orders = data?.calastoneOrders?.calastoneOrders ?? [];
  const pageInfo = data?.calastoneOrders?.pageInfo;
  const isLoading = loading || networkStatus === NetworkStatus.fetchMore;

  return (
    <AdminPageContainer>
      <AdminPageHeader
        title="Calastone Orders"
        subtitle="Calastone orders associated with transfers."
      />
      <DataTable
        data={orders}
        columns={columns}
        isLoadingData={isLoading}
        manualSorting={true}
        manualPagination={true}
        hidePagination={true}
        pageSize={10_000}
        className="overflow-hidden border"
        onRowClick={(row) => {
          void navigate({
            to: "/calastone-orders/$calastoneOrderId",
            params: { calastoneOrderId: row.original.id },
          });
        }}
      />
      <div className="mt-4 flex w-full justify-center">
        {pageInfo?.hasNextPage ? (
          <Button
            variant="outline"
            onClick={() =>
              fetchMore({
                variables: {
                  after: pageInfo.endCursor,
                  first: PAGE_SIZE,
                },
              })
            }
            loading={isLoading}
          >
            Load more
          </Button>
        ) : (
          orders.length > 0 && (
            <p className="text-secondary text-sm">No more data</p>
          )
        )}
      </div>
    </AdminPageContainer>
  );
}
